import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError, getErrorMessage } from '../../utils/errorUtils';
import EditableSalesOrderLines from '../../components/Sales/EditableSalesOrderLines';
import CreateInvoiceModal from '../../components/Sales/CreateInvoiceModal';
import CreateProductionOrdersModal from '../../components/Sales/CreateProductionOrdersModal';
import EntityLink from '../../components/Common/EntityLink';
import BackButton from '../../components/Common/BackButton';
import PickListViewer from '../../components/PickLists/PickListViewer';
import { useMessages } from '../../components/Messages';
import './SalesOrders.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import { fetchAllCustomers } from '../../utils/fetchAllPages';

const ViewSalesOrder = forwardRef(({ orderId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('sales');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError, showWarning } = useMessages();
  const [order, setOrder] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  // error: the order could not be loaded (nothing to show). saveError: a save/delete failed; the
  // order stays on screen with the message above it
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [lines, setLines] = useState([]);
  const [linesChanged, setLinesChanged] = useState(false); // only edited lines are sent (#359)
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [generatingPickList, setGeneratingPickList] = useState(false);
  const [pickLists, setPickLists] = useState([]);
  const [viewPickListId, setViewPickListId] = useState(null);
  const [linkedPOs, setLinkedPOs] = useState(null);
  const [showCreateProductionOrders, setShowCreateProductionOrders] = useState(false);

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    setSaveError(null);
    fetchCustomers();
    if (orderId && !createMode) {
      fetchOrder();
    } else if (createMode) {
      // so_number is assigned by the API when the order is saved
      const newOrder = {
        customer_id: null,
        order_date: localToday(),
        requested_delivery_date: localToday(30),
        status: 'DRAFT',
        payment_terms: 'NET_30',
        currency_code: 'USD',
        tax_amount: 0,
        total_amount: 0,
        notes: '',
        po_number: ''
      };
      setOrder(newOrder);
      setEditData(newOrder);
      setLines([]);
    }
  }, [orderId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  // Helper function to format datetime to date for HTML date inputs
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toISOString().split('T')[0];
  };

  const fetchCustomers = async () => {
    try {
      // Only active customers can be chosen (all pages, #310, #384); the order's own customer is added back below if inactive
      setCustomers(await fetchAllCustomers({ is_active: 'true' }));
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchContacts = async (customerId) => {
    if (!customerId) {
      setContacts([]);
      return;
    }
    try {
      const response = await apiFetch(getApiUrl(`/api/contacts/?customer_id=${customerId}`));
      if (response.ok) {
        const data = await response.json();
        setContacts(data || []);
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}`));
      if (response.ok) {
        const data = await response.json();
        setOrder(data);
        setEditData(data);
        // Use lines from the main response
        if (data.lines) {
          setLines(data.lines);
        }
        setLinesChanged(false);
        // Fetch contacts if customer is set
        if (data.customer_id) {
          await fetchContacts(data.customer_id);
        }
        // Fetch pick lists for non-draft orders
        if (data.status && data.status !== 'DRAFT') {
          await fetchPickLists();
        }
      } else {
        setError(t('salesOrders.view.messages.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching sales order:', error);
      setError(t('salesOrders.view.messages.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);

    // Update customer name when customer changes
    if (field === 'customer_id') {
      const customer = customers.find(c => c.customer_id === parseInt(value));
      if (customer) {
        setEditData(prev => ({
          ...prev,
          customer_name: customer.customer_name,
          // the customer's payment terms, as in quotations (#359)
          ...(createMode && customer.payment_terms && { payment_terms: customer.payment_terms })
        }));
        // Fetch contacts for the selected customer
        fetchContacts(parseInt(value));
      } else {
        setContacts([]);
      }
    }
  };

  // Quantities and prices can carry decimals (e.g. 2.5 millares); accept a comma as decimal separator
  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    return Number(String(value).trim().replace(',', '.'));
  };

  // Line payload as the API expects it (SalesOrderLineCreate / SalesOrderLineEdit)
  const buildLinesPayload = () => lines.map((line, index) => ({
    ...(line.sales_order_line_id ? { sales_order_line_id: line.sales_order_line_id } : {}),
    line_number: index + 1,
    product_id: parseInt(line.product_id),
    product_code: line.product_code || null,
    product_name: line.product_name || line.description || line.product_code || String(line.product_id),
    description: line.description || null,
    quantity_ordered: toNumber(line.quantity_ordered),
    uom_code: line.uom_code || 'PCS',
    unit_price: toNumber(line.unit_price) || 0,
    discount_percentage: toNumber(line.discount_percentage) || 0,
    tax_percentage: toNumber(line.tax_percentage) || 0,
    notes: line.notes || null
  }));

  const validateLines = () => {
    if (lines.length === 0) return t('salesOrders.view.messages.linesRequired');
    for (const [index, line] of lines.entries()) {
      if (!line.product_id) return t('salesOrders.view.messages.lineProductRequired', { line: index + 1 });
      const quantity = toNumber(line.quantity_ordered);
      if (!(quantity > 0)) return t('salesOrders.view.messages.lineQuantityRequired', { line: index + 1 });
    }
    return null;
  };

  const showStockShortages = (shortages) => {
    if (!shortages || shortages.length === 0) return;
    const detail = shortages
      .map(s => t('salesOrders.view.messages.stockShortageLine', {
        product: s.product_code || s.product_name,
        missing: parseFloat(s.missing_quantity).toLocaleString(),
        free: parseFloat(s.free_quantity).toLocaleString()
      }))
      .join('; ');
    showWarning(t('salesOrders.view.messages.stockShortageTitle'), t('salesOrders.view.messages.stockShortageMessage', { detail }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!editData.customer_id) {
      showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.customerRequired'));
      return;
    }
    const linesError = validateLines();
    if (linesError) {
      showError(t('salesOrders.view.messages.validationError'), linesError);
      return;
    }

    const failedTitle = createMode ? t('salesOrders.view.messages.failedToCreateSalesOrder') : t('salesOrders.view.messages.failedToUpdateSalesOrder');
    try {
      const customerId = parseInt(editData.customer_id);

      if (createMode) {
        // The API creates the header and its lines in one request
        // so_number is assigned by the API; empty form fields go as null so optional dates validate
        const { so_number, status, lines: _lines, ...rest } = editData;
        const header = Object.fromEntries(Object.entries(rest).map(([key, value]) => [key, value === '' ? null : value]));
        const response = await apiFetch(getApiUrl('/api/sales-orders/'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...header, customer_id: customerId, lines: buildLinesPayload() })
        });
        if (!response.ok) {
          const parsed = await showApiError(response, showError, tCommon, failedTitle);
          setSaveError(getErrorMessage(parsed, tCommon));
          return;
        }
        const result = await response.json();
        setHasChanges(false);
        setSaveError(null);
        showSuccess(
          t('salesOrders.view.messages.salesOrderCreated'),
          t('salesOrders.view.messages.orderCreatedSuccess', { orderNumber: result.so_number })
        );
        showStockShortages(result.stock_shortages);
        const saved = result.sales_order || { sales_order_id: result.sales_order_id };
        setOrder(saved);
        setEditData(saved);
        if (onSave) onSave(saved);
        return;
      }

      const response = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editData, customer_id: customerId })
      });
      if (!response.ok) {
        const parsed = await showApiError(response, showError, tCommon, failedTitle);
        setSaveError(getErrorMessage(parsed, tCommon));
        return;
      }

      // Lines are saved as a whole set: updated, added and removed in one transaction. Only when they were
      // edited: past CONFIRMED the API refuses line edits, and a header-only change must not fail (#359)
      if (linesChanged) {
        const linesResponse = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}/lines`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lines: buildLinesPayload() })
        });
        if (!linesResponse.ok) {
          const parsed = await showApiError(linesResponse, showError, tCommon, t('salesOrders.view.messages.failedToSaveLines'));
          setSaveError(getErrorMessage(parsed, tCommon));
          await fetchOrder();
          return;
        }
      }

      setHasChanges(false);
      setLinesChanged(false);
      setSaveError(null);
      showSuccess(
        t('salesOrders.view.messages.salesOrderUpdated'),
        t('salesOrders.view.messages.orderUpdatedSuccess', { orderNumber: editData.so_number })
      );
      await fetchOrder();
      if (onSave) onSave(editData);
    } catch (error) {
      console.error('Error saving sales order:', error);
      // Network error: keep the form (and what was typed) and show the problem above it
      const errorMessage = t('salesOrders.view.messages.savingError');
      setSaveError(errorMessage);
      showError(failedTitle, errorMessage);
    }
  };

  const handleCancel = () => {
    setSaveError(null);
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(order);
      setHasChanges(false);
      setLinesChanged(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('salesOrders.view.messages.deleteOrderConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        if (onBack) onBack();
      } else {
        // e.g. 409/422 when it has related documents: the API says why; the order stays on screen
        const parsed = await showApiError(response, showError, tCommon, t('salesOrders.view.messages.failedToDeleteOrder'));
        setSaveError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error deleting sales order:', error);
      const errorMessage = t('salesOrders.view.messages.deletingError');
      setSaveError(errorMessage);
      showError(t('salesOrders.view.messages.failedToDeleteOrder'), errorMessage);
    }
  };

  const handleCreateInvoice = () => {
    setShowCreateInvoiceModal(true);
  };

  const handleInvoiceCreated = (invoice) => {
    // Refresh the sales order to update any status changes
    fetchOrder();
    // Optionally navigate to the invoice or show it in context
    showSuccess(t('salesOrders.view.messages.invoiceCreated'), t('salesOrders.view.messages.invoiceCreatedSuccess', { invoiceNumber: invoice.invoice_number }));
  };

  const handleAllocateStock = async () => {
    if (!orderId) {
      showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.noOrderId') + ' for allocation');
      return;
    }

    setAllocating(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}/allocate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();

        // Refresh the order data to show updated allocation status
        await fetchOrder();

        // Show success message with allocation details
        const allocatedLines = result.allocated_lines || 0;
        const totalLines = result.total_lines || 0;
        // A line counts only when fully allocated; the short ones are listed with their quantities (#305)
        const shortLines = (result.results || [])
          .filter((r) => !r.success)
          .map((r) => t('salesOrders.view.messages.partialAllocationLine', {
            product: r.product_code || r.line_id,
            allocated: Number(r.quantity_allocated || 0).toLocaleString(),
            ordered: Number(r.quantity_ordered || 0).toLocaleString(),
          }))
          .join('; ');

        if (result.all_allocated) {
          showSuccess(
            t('salesOrders.view.messages.stockAllocatedSuccess'),
            t('salesOrders.view.messages.allLinesAllocated', { count: allocatedLines })
          );
        } else {
          showWarning(
            t('salesOrders.view.messages.partialAllocation'),
            t('salesOrders.view.messages.partialAllocationMessage', { allocated: allocatedLines, total: totalLines, lines: shortLines })
          );
        }
      } else {
        await showApiError(response, showError, tCommon, t('salesOrders.view.messages.allocationFailed'));
      }
    } catch (error) {
      console.error('Allocation error:', error);
      showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.allocationError'));
    } finally {
      setAllocating(false);
    }
  };

  const handleGeneratePickList = async () => {
    if (!orderId) {
      showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.noOrderId') + ' for pick list generation');
      return;
    }

    // Check if order is in the correct status
    if (editData.status !== 'ALLOCATED') {
      showError(
        t('salesOrders.view.messages.invalidOrderStatus'),
        t('salesOrders.view.messages.pickListStatusError', { status: editData.status })
      );
      return;
    }

    // Check if a pick list already exists for this order
    if (pickLists.length > 0) {
      const existingPickList = pickLists.find(pl => pl.status === 'PENDING' || pl.status === 'IN_PROGRESS');
      if (existingPickList) {
        showWarning(
          t('salesOrders.view.messages.pickListExists'),
          t('salesOrders.view.messages.pickListExistsMessage', { pickListNumber: existingPickList.pick_list_number })
        );
        return;
      }
    }

    setGeneratingPickList(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}/generate-pick-list`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();

        // Refresh the order data to show updated status
        await fetchOrder();

        // Refresh pick lists to show the newly created one
        await fetchPickLists();

        // Show success message with pick list details
        const pickListId = result.pick_list_id;
        const pickListNumber = result.pick_list_number || pickListId;

        showSuccess(
          t('salesOrders.view.messages.pickListGeneratedSuccess'),
          t('salesOrders.view.messages.pickListGenerated', { pickListNumber })
        );
      } else {
        await showApiError(response, showError, tCommon, t('salesOrders.view.messages.pickListGenerationFailed'));
      }
    } catch (error) {
      console.error('Pick list generation error:', error);
      showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.pickListError'));
    } finally {
      setGeneratingPickList(false);
    }
  };

  const fetchPickLists = async () => {
    if (!orderId) return;

    try {
      const response = await apiFetch(getApiUrl(`/api/sales-orders/${orderId}/pick-lists`));
      if (response.ok) {
        const data = await response.json();
        setPickLists(data.pick_lists || []);
      } else {
        // Only show error for non-404 errors (404 is expected when no pick lists exist)
        if (response.status !== 404) {
          console.error('Failed to fetch pick lists:', response.status);
          showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.failedToLoadPickLists'));
        }
        setPickLists([]);
      }
    } catch (error) {
      console.error('Error fetching pick lists:', error);
      // Only show error message if it's not a network issue during initial load
      if (editData.status && editData.status !== 'DRAFT') {
        showError(t('salesOrders.view.messages.validationError'), t('salesOrders.view.messages.unableToLoadPickLists'));
      }
      setPickLists([]);
    }
  };

  const fetchLinkedPOs = async (force = false) => {
    if (!orderId || (linkedPOs !== null && !force)) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders?sales_order_id=${orderId}&page_size=50`));
      setLinkedPOs(response.ok ? (await response.json()).orders || [] : []);
    } catch { setLinkedPOs([]); }
  };

  const updateOrderTotals = useCallback((updatedLines) => {
    let subtotalBeforeTax = 0;
    let totalTaxAmount = 0;
    let discountAmount = 0;

    updatedLines.forEach(line => {
      const lineGross = (line.quantity_ordered || 0) * (line.unit_price || 0);
      const lineDiscount = lineGross * ((line.discount_percentage || 0) / 100);
      const lineAfterDiscount = lineGross - lineDiscount;
      const lineTax = lineAfterDiscount * ((line.tax_percentage || 0) / 100);

      subtotalBeforeTax += lineAfterDiscount;
      totalTaxAmount += lineTax;
      discountAmount += lineDiscount;
    });

    const finalTotal = subtotalBeforeTax + totalTaxAmount;

    setEditData(prev => ({
      ...prev,
      subtotal: subtotalBeforeTax,
      discount_amount: discountAmount,
      tax_amount: totalTaxAmount,
      total_amount: finalTotal
    }));
  }, []);

  const handleLinesChange = useCallback((updatedLines) => {
    setLines(updatedLines);
    setHasChanges(true);
    setLinesChanged(true);
    updateOrderTotals(updatedLines);
  }, [updateOrderTotals]);

  // The order's customer stays in the select even if it is no longer active (#384)
  const customerOptions = editData.customer_id && !customers.some(c => c.customer_id === parseInt(editData.customer_id))
    ? [...customers, { customer_id: parseInt(editData.customer_id), customer_name: editData.customer_name || order?.customer_name }]
    : customers;

  // Lines still to produce that have no production order yet (#333)
  const linesPendingProduction = lines.filter(line =>
    (parseFloat(line.quantity_to_produce) || 0) > 0 && !line.production_order_line_id);
  const canCreateProductionOrders = ['CONFIRMED', 'IN_PRODUCTION'].includes(editData.status)
    && linesPendingProduction.length > 0;

  const handleProductionOrdersCreated = async () => {
    await fetchOrder();
    await fetchLinkedPOs(true);
    setActiveTab('production-orders');
  };

  const renderGeneralTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('salesOrders.view.formLabels.orderInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.orderNumber')}</label>
            <input
              type="text"
              value={createMode ? '' : editData.so_number || ''}
              placeholder={createMode ? t('salesOrders.view.formValues.soNumberAssignedOnSave') : ''}
              disabled
            />
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.customer')} *</label>
            <select
              value={editData.customer_id || ''}
              onChange={(e) => handleFieldChange('customer_id', e.target.value)}
              disabled={!editMode && !createMode}
              required
            >
              <option value="" disabled hidden>{t('salesOrders.view.formValues.selectCustomer')}</option>
              {customerOptions.map(customer => (
                <option key={customer.customer_id} value={customer.customer_id}>
                  {customer.customer_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.orderDate')} *</label>
            {editMode || createMode ? (
              <input
                type="date"
                value={formatDateForInput(editData.order_date)}
                onChange={(e) => handleFieldChange('order_date', e.target.value)}
                required
              />
            ) : (
              <span className="form-value">
                {editData.order_date ? formatAppDate(editData.order_date) : t('salesOrders.view.formValues.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.requestedDeliveryDate')} *</label>
            {editMode || createMode ? (
              <input
                type="date"
                value={formatDateForInput(editData.requested_delivery_date)}
                onChange={(e) => handleFieldChange('requested_delivery_date', e.target.value)}
                required
              />
            ) : (
              <span className="form-value">
                {editData.requested_delivery_date ? formatAppDate(editData.requested_delivery_date) : t('salesOrders.view.formValues.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.poNumber')}</label>
            <input
              type="text"
              value={editData.po_number || ''}
              onChange={(e) => handleFieldChange('po_number', e.target.value)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.status')}</label>
            <select
              value={editData.status || 'DRAFT'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="DRAFT">{t('salesOrders.view.statusOptions.draft')}</option>
              <option value="CONFIRMED">{t('salesOrders.view.statusOptions.confirmed')}</option>
              <option value="IN_PRODUCTION">{t('salesOrders.view.statusOptions.inProduction')}</option>
              <option value="ALLOCATED">{t('salesOrders.view.statusOptions.allocated')}</option>
              <option value="PICKING">{t('salesOrders.view.statusOptions.picking')}</option>
              <option value="PACKED">{t('salesOrders.view.statusOptions.packed')}</option>
              <option value="SHIPPED">{t('salesOrders.view.statusOptions.shipped')}</option>
              <option value="INVOICED">{t('salesOrders.view.statusOptions.invoiced')}</option>
              <option value="DELIVERED">{t('salesOrders.view.statusOptions.delivered')}</option>
              <option value="CANCELLED">{t('salesOrders.view.statusOptions.cancelled')}</option>
              <option value="ON_HOLD">{t('salesOrders.view.statusOptions.onHold')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.currency')}</label>
            <select
              value={editData.currency_code || 'USD'}
              onChange={(e) => handleFieldChange('currency_code', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="USD">{t('common.currencies.usd')}</option>
              <option value="EUR">{t('common.currencies.eur')}</option>
              <option value="GBP">{t('common.currencies.gbp')}</option>
              <option value="SEK">{t('common.currencies.sek')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('salesOrders.view.formLabels.termsAndSettings')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.paymentTerms')}</label>
            <select
              value={editData.payment_terms || 'NET_30'}
              onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="UPFRONT">{t('common.paymentTerms.paymentUpfront')}</option>
              <option value="NET_30">{t('common.paymentTerms.net30')}</option>
              <option value="NET_45">{t('common.paymentTerms.net45')}</option>
              <option value="NET_60">{t('common.paymentTerms.net60')}</option>
              <option value="NET_90">{t('common.paymentTerms.net90')}</option>
              <option value="NET_120">{t('common.paymentTerms.net120')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('salesOrders.view.formLabels.priority')}</label>
            <select
              value={editData.priority || 'NORMAL'}
              onChange={(e) => handleFieldChange('priority', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="LOW">{t('salesOrders.view.priorityOptions.low')}</option>
              <option value="NORMAL">{t('salesOrders.view.priorityOptions.normal')}</option>
              <option value="HIGH">{t('salesOrders.view.priorityOptions.high')}</option>
              <option value="URGENT">{t('salesOrders.view.priorityOptions.urgent')}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLinesTab = () => (
    <div className="tab-content">
      <EditableSalesOrderLines
        data={lines}
        parentEntity={order || { sales_order_id: orderId }}
        editMode={editMode || createMode}
        onLinesChange={handleLinesChange}
      />
    </div>
  );

  const renderContactsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('salesOrders.view.formLabels.orderContacts')}</h3>

        {!editData.customer_id ? (
          <div className="no-data">{t('salesOrders.view.contactsMessages.selectCustomerFirst')}</div>
        ) : contacts.length === 0 ? (
          <div className="no-data">{t('salesOrders.view.contactsMessages.noContactsFound')}</div>
        ) : (
          <div className="contacts-list">
            <div className="form-group">
              <label>{t('salesOrders.view.formLabels.primaryContact')}</label>
              <select
                value={editData.contact_id || ''}
                onChange={(e) => handleFieldChange('contact_id', e.target.value)}
                disabled={!editMode && !createMode}
              >
                <option value="" disabled hidden>{t('salesOrders.view.formValues.selectContact')}</option>
                {contacts.map(contact => (
                  <option key={contact.contact_id} value={contact.contact_id}>
                    {contact.first_name} {contact.last_name} {contact.position ? `(${contact.position})` : ''} - {contact.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="contacts-table">
              <h4>{t('salesOrders.view.formLabels.availableContacts')}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t('quotations.view.tableHeaders.name')}</th>
                    <th>{t('quotations.view.tableHeaders.position')}</th>
                    <th>{t('quotations.view.tableHeaders.department')}</th>
                    <th>{t('quotations.view.tableHeaders.email')}</th>
                    <th>{t('quotations.view.tableHeaders.phone')}</th>
                    <th>{t('quotations.view.tableHeaders.primary')}</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(contact => (
                    <tr key={contact.contact_id} className={contact.contact_id === parseInt(editData.contact_id) ? 'selected' : ''}>
                      <td>{contact.first_name} {contact.last_name}</td>
                      <td>{contact.position || '-'}</td>
                      <td>{contact.department || '-'}</td>
                      <td>{contact.email || '-'}</td>
                      <td>{contact.phone || '-'}</td>
                      <td>{contact.is_primary ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('salesOrders.view.formLabels.notesAndTerms')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('salesOrders.view.formLabels.internalNotes')}</label>
            <textarea
              value={editData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              disabled={!editMode && !createMode}
              rows="4"
            />
          </div>
          <div className="form-group full-width">
            <label>{t('salesOrders.view.formLabels.termsAndConditions')}</label>
            <textarea
              value={editData.terms_conditions || ''}
              onChange={(e) => handleFieldChange('terms_conditions', e.target.value)}
              disabled={!editMode && !createMode}
              rows="6"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderProductionOrdersTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('linkedProductionOrders.title')}</h3>
        {linkedPOs === null ? (
          <div className="loading">{t('linkedProductionOrders.loading')}</div>
        ) : linkedPOs.length === 0 ? (
          <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('linkedProductionOrders.noneForSalesOrder')}</div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{t('production:fields.poNumber')}</th>
                <th>{t('common:ui.status')}</th>
                <th>{t('common:ui.priority')}</th>
                <th>{t('invoices.view.fields.dueDate')}</th>
                <th>{t('common:ui.customer')}</th>
              </tr>
            </thead>
            <tbody>
              {linkedPOs.map(po => (
                <tr key={po.production_order_id}>
                  <td>
                    <EntityLink
                      type="production-order"
                      id={po.po_number}
                      label={po.po_number}
                      returnLabel={order?.so_number}
                      returnType="sales-order"
                      returnId={orderId}
                    />
                  </td>
                  <td><span className={`status-badge status-${po.status?.toLowerCase()}`}>{po.status}</span></td>
                  <td>{po.priority}</td>
                  <td>{po.planned_due_date ? formatAppDate(po.planned_due_date) : '-'}</td>
                  <td>{po.customer_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('salesOrders.view.loading')}</div></div>;
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : t('salesOrders.view.messages.loadingError');
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!order && !createMode) {
    return <div className="view-container"><div className="no-data">{t('salesOrders.view.formValues.noSalesOrderSelected')}</div></div>;
  }

  return (
    <div className="view-container">
      {saveError && <div className="error-message" role="alert">{saveError}</div>}
      <div className="view-header">
        <div className="header-title">
          <BackButton onBack={onBack} entityType="sales-order" entityIds={[orderId, order?.sales_order_id]} />
          <h1>
            {createMode ? t('salesOrders.view.formValues.newSalesOrder') : editData.so_number || t('salesOrders.view.formValues.salesOrder')}
          </h1>
          {!createMode && (
            <span className={`status-badge ${
              editData.status === 'CONFIRMED' ? 'status-completed' :
              editData.status === 'IN_PRODUCTION' ? 'status-active' :
              editData.status === 'ALLOCATED' ? 'status-info' :
              editData.status === 'PICKING' ? 'status-info' :
              editData.status === 'PACKED' ? 'status-info' :
              editData.status === 'SHIPPED' ? 'status-success' :
              editData.status === 'INVOICED' ? 'status-success' :
              editData.status === 'DELIVERED' ? 'status-success' :
              editData.status === 'CANCELLED' ? 'status-cancelled' :
              editData.status === 'ON_HOLD' ? 'status-warning' :
              'status-pending'
            }`}>
              {editData.status || 'DRAFT'}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              {(editData.status === 'CONFIRMED' || editData.status === 'IN_PRODUCTION') && editData.status !== 'ALLOCATED' && (
                <button
                  className="btn-action"
                  onClick={handleAllocateStock}
                  disabled={allocating}
                >
                  {allocating ? t('salesOrders.view.buttons.allocating') : t('salesOrders.view.buttons.allocateStock')}
                </button>
              )}
              {canCreateProductionOrders && (
                <button className="btn-action" onClick={() => setShowCreateProductionOrders(true)}>
                  {t('salesOrders.view.buttons.createProductionOrders')}
                </button>
              )}
              {editData.status === 'ALLOCATED' && (
                <button
                  className="btn-action"
                  onClick={handleGeneratePickList}
                  disabled={generatingPickList}
                >
                  {generatingPickList ? t('salesOrders.view.buttons.generating') : t('salesOrders.view.buttons.generatePickList')}
                </button>
              )}
              {editData.status === 'SHIPPED' && (
                <button className="btn-action" onClick={handleCreateInvoice}>
                  📄 {t('salesOrders.view.buttons.createInvoice')}
                </button>
              )}
              <button className="btn-secondary" onClick={onEdit}>{t('salesOrders.toolbar.buttons.edit')}</button>
              <button className="btn-danger" onClick={handleDelete}>{t('salesOrders.toolbar.buttons.delete')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('salesOrders.toolbar.buttons.cancel')}</button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={(!hasChanges && !createMode) || !editData.customer_id}
                title={!editData.customer_id ? t('salesOrders.view.formValues.customerSelectFirst') : ''}
              >
                {createMode ? t('salesOrders.toolbar.buttons.create') : t('salesOrders.toolbar.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            {t('salesOrders.view.tabs.general')}
          </button>
          <button
            className={`tab-item ${activeTab === 'lines' ? 'active' : ''}`}
            onClick={() => setActiveTab('lines')}
          >
            {t('salesOrders.view.tabs.lines')}
          </button>
          <button
            className={`tab-item ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            {t('salesOrders.view.tabs.contacts')}
          </button>
          <button
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            {t('salesOrders.view.tabs.notes')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'production-orders' ? 'active' : ''}`}
              onClick={() => { setActiveTab('production-orders'); fetchLinkedPOs(); }}
            >
              {t('linkedProductionOrders.title')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'lines' && renderLinesTab()}
          {activeTab === 'contacts' && renderContactsTab()}
          {activeTab === 'notes' && renderNotesTab()}
          {activeTab === 'production-orders' && renderProductionOrdersTab()}
        </div>
      </div>

      {showCreateInvoiceModal && (
        <CreateInvoiceModal
          salesOrder={order}
          onClose={() => setShowCreateInvoiceModal(false)}
          onInvoiceCreated={handleInvoiceCreated}
        />
      )}

      {showCreateProductionOrders && (
        <CreateProductionOrdersModal
          salesOrder={order}
          lines={linesPendingProduction}
          onClose={() => setShowCreateProductionOrders(false)}
          onSuccess={handleProductionOrdersCreated}
        />
      )}

      {viewPickListId && (
        <PickListViewer
          pickListId={viewPickListId}
          onClose={() => {
            setViewPickListId(null);
            fetchPickLists();
          }}
        />
      )}
    </div>
  );
});

ViewSalesOrder.displayName = 'ViewSalesOrder';

export default ViewSalesOrder;